import { Field } from '@odin-labs/components';
import React from 'react';

export type NameFieldProps = {
  /** The committed name; edits are held locally until blur or Enter. */
  value: string;
  placeholder?: string;
  error: string | null;
  /** Save has been tried: the error shows whether or not the field was touched, as a submitted form's do. */
  submitted?: boolean;
  disabled: boolean;
  /** Resolves false when the layout refused the name; the field then shows the committed name again. */
  onCommit: (name: string) => Promise<boolean> | void;
};

/**
 * Inline name editing for a zone or entry. Typing is local so the caret does
 * not jump while the layout refetches; the value commits on blur or Enter. A
 * name the layout refuses, such as one another zone already has, is dropped
 * for the committed one. The error waits until the field is touched (edited,
 * then left) or Save is tried, as the app's react-hook-form forms show none
 * before a submit.
 */
export function NameField({
  value,
  placeholder,
  error,
  submitted = false,
  disabled,
  onCommit,
}: NameFieldProps): React.ReactElement {
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => setDraft(value), [value]);
  const edited = React.useRef(false);
  const [touched, setTouched] = React.useState(false);

  const commit = async (): Promise<void> => {
    if (draft === value) return;
    const committed = await onCommit(draft);
    if (committed === false) setDraft(value);
  };

  return (
    <Field
      name="name"
      label="Name"
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      error={(touched || submitted) && error ? error : undefined}
      onChange={(next) => {
        edited.current = true;
        setDraft(next);
      }}
      onBlur={() => {
        if (edited.current) setTouched(true);
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
      }}
    />
  );
}
